import React, { useState } from "react";
import {
  getAuth,
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { ensureUserProfile, stashPendingRegistration } from "../shared/provisionUser";
import googleButton from "../../images/web_light_sq_SU@2x.png";
import logo from "../../images/logo.svg";
import "./auth.css";

const TITLES = ["Ms.", "Miss", "Mrs.", "Mr.", "Dr.", "Prof.", "Other"];

const RegistrationForm = ({ onSwitchToLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [title, setTitle] = useState("Ms.");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      setError("Please agree to the Terms and Privacy Policy to continue.");
      return;
    }
    try {
      const auth = getAuth();
      await createUserWithEmailAndPassword(auth, email, password);

      const displayName =
        title === "Other" ? `${firstName} ${lastName}` : `${title} ${lastName}`;

      await updateProfile(auth.currentUser, { displayName });

      // Gate real setup behind a verified email: this stops the steady drip of
      // bot sign-ups from ever creating a school / users doc / sample classroom.
      // The profile is provisioned (with these fields) once they click the link
      // — see ensureUserProfile + the verification effect in UserContext. The
      // form fields can't ride along on the Auth user, so stash them locally.
      await sendEmailVerification(auth.currentUser);
      stashPendingRegistration({ email, title, firstName, lastName });

      // They're signed in but unverified — the app-wide gate now shows the
      // "verify your email" screen until the link is clicked.
      navigate("/");
    } catch (err) {
      console.error("Error registering user:", err);
      setError("Registration failed. Please try again.");
    }
  };

  const handleGoogleSignIn = async () => {
    if (!accepted) {
      setError("Please agree to the Terms and Privacy Policy to continue.");
      return;
    }
    try {
      const auth = getAuth();
      const result = await signInWithPopup(auth, new GoogleAuthProvider());

      // Google verifies the address, so no email gate is needed — provision the
      // profile + sample classroom immediately, then straight into onboarding
      // (title is collected there if missing).
      await ensureUserProfile(result.user);
      navigate("/getstarted");
    } catch (err) {
      console.error("Error with Google sign-in:", err);
      setError("Google sign-in failed. Please try again.");
    }
  };

  // Cross-link to Login: switch the modal if we're in one, else navigate.
  const goToLogin = () => {
    if (onSwitchToLogin) onSwitchToLogin();
    else navigate("/loginform");
  };

  return (
    <div className="auth-card">
      <div className="auth-head">
        <img src={logo} className="auth-logo" alt="classBOON" />
        <h1 className="auth-title">Create your account</h1>
        <p className="auth-sub">Set up your classroom in a couple of minutes.</p>
      </div>

      <form className="auth-form" onSubmit={handleSubmit}>
        <div className="auth-field">
          <label htmlFor="reg-title">Title</label>
          <select id="reg-title" value={title} onChange={(e) => setTitle(e.target.value)} required>
            {TITLES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="auth-row">
          <div className="auth-field">
            <label htmlFor="reg-first">First name</label>
            <input id="reg-first" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} required placeholder="First name" />
          </div>
          <div className="auth-field">
            <label htmlFor="reg-last">Last name</label>
            <input id="reg-last" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} required placeholder="Last name" />
          </div>
        </div>
        <div className="auth-field">
          <label htmlFor="reg-email">Email</label>
          <input id="reg-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@school.org" />
        </div>
        <div className="auth-field">
          <label htmlFor="reg-pass">Password</label>
          <input id="reg-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Create a password" />
        </div>
        <label className="auth-consent">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            required
          />
          <span>
            I agree to the{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{" "}
            and{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
          </span>
        </label>

        <button type="submit" className="auth-submit">Create Account</button>
      </form>

      <div className="auth-divider">or</div>
      <button onClick={handleGoogleSignIn} className="auth-google">
        <img src={googleButton} alt="Sign up with Google" />
      </button>

      {error && <p className="auth-error">{error}</p>}

      <div className="auth-foot">
        Already have an account?{" "}
        <button type="button" className="auth-linkbtn" onClick={goToLogin}>Sign in</button>
      </div>
    </div>
  );
};

export default RegistrationForm;
